
import Link from '@/components/compat/Link';
import { ArrowRight, Sparkles } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import PowerCard from '@/components/help-center/powers/PowerCard';
import { DiscoverDialogClose } from '@/components/help-center/DiscoverDialogClose';
import { type PowerInfo } from '@/lib/powers-data';
import { smilieProxyUrl } from '@/lib/smilie-proxy-url';
import { cn } from '@/lib/utils';

type FloatingSmileyDiscoverDialogProps = {
  smileyCode: string | null;
  power: PowerInfo | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

function SmileyPreview({ code, size = 72 }: { code: string; size?: number }) {
  const src = smilieProxyUrl(code, 40);

  return (
    <img
      src={src}
      alt=""
      width={size}
      height={size}
      className="object-contain drop-shadow-[0_4px_16px_rgba(0,0,0,0.35)]"
      style={{ width: size, height: size }}
      draggable={false}
    />
  );
}

export default function FloatingSmileyDiscoverDialog({
  smileyCode,
  power,
  open,
  onOpenChange,
}: FloatingSmileyDiscoverDialogProps) {
  if (!smileyCode) return null;

  const label = smileyCode.charAt(0).toUpperCase() + smileyCode.slice(1);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn(
          'max-h-[90vh] overflow-y-auto border-0 p-0 shadow-none [&>button]:hidden',
          power
            ? 'max-w-[min(100vw-2rem,340px)] bg-transparent'
            : 'relative max-w-md bg-white p-6 sm:rounded-2xl'
        )}
      >
        {power ? (
          <div className="relative">
            <DialogTitle className="sr-only">
              Descoberta: {power.name}
            </DialogTitle>
            <p className="mb-3 text-center font-poppins text-xs font-semibold uppercase tracking-widest text-sky-200/90" aria-hidden>
              Descoberta
            </p>
            <PowerCard
              power={power}
              overlay={
                <DiscoverDialogClose
                  className="right-2.5 top-2.5"
                  onClick={(e) => e.stopPropagation()}
                />
              }
            />
          </div>
        ) : (
          <>
            <DiscoverDialogClose className="right-4 top-4" />
            <DialogHeader className="items-center space-y-4 text-center">
              <div className="flex h-24 w-24 items-center justify-center rounded-2xl border border-gray-100 bg-gray-50">
                <SmileyPreview code={smileyCode} size={64} />
              </div>
              <div className="space-y-2">
                <DialogTitle className="font-poppins text-xl text-gray-900">
                  ({label})
                </DialogTitle>
                <DialogDescription className="font-poppins text-sm leading-relaxed text-gray-600">
                  Este smiley faz parte do universo xat. Explore a Powerpédia para
                  descobrir powers, coleções e recursos relacionados.
                </DialogDescription>
              </div>
            </DialogHeader>

            <div className="mt-6 flex flex-col gap-2">
              <Link
                href="/powers"
                onClick={() => onOpenChange(false)}
                className={cn(
                  'inline-flex w-full items-center justify-center gap-2 rounded-xl px-4 py-3',
                  'bg-[#030023] font-poppins text-sm font-semibold text-white',
                  'transition-colors hover:bg-[#0a1450]'
                )}
              >
                <Sparkles className="h-4 w-4 text-sky-300" aria-hidden />
                Explorar Powerpédia
                <ArrowRight className="h-4 w-4 text-sky-300/80" aria-hidden />
              </Link>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
